import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaSave, FaUndo, FaPlus, FaTrash, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { LuGift } from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';

const SLUG = 'refer-and-earn';

const emptyStep = (num, order) => ({
  num,
  title: '',
  description: '',
  order,
  isActive: true,
});

const defaultForm = () => ({
  isPublished: true,
  pageTitle: 'Refer & Earn',
  pageSubtitle: '',
  pageIcon: 'LuGift',
  loadingMessage: '',
  statsLabels: {
    totalReferrals: 'Total Referrals',
    pending: 'Pending',
    successful: 'Successful',
    rewardPoints: 'Reward Points',
  },
  referralLinkSection: {
    title: 'Your Referral Link',
    description: '',
    yourCodePrefix: 'Your Code:',
    copyCodeLabel: 'Copy',
    sendInviteButton: 'Send Invitation Email',
  },
  howItWorks: {
    title: 'How It Works',
    steps: [emptyStep(1, 0), emptyStep(2, 1), emptyStep(3, 2), emptyStep(4, 3)],
  },
  referralsTable: {
    title: 'My Referrals',
    columns: {
      email: 'Email',
      name: 'Name',
      status: 'Status',
      invitedOn: 'Invited On',
      registered: 'Registered',
      reward: 'Reward',
    },
  },
  statusLabels: {
    pending: 'Invitation Sent',
    registered: 'Signed Up',
    purchased: 'Made Purchase',
    rewarded: 'Reward Earned',
  },
  emptyState: { description: '' },
  inviteModal: {},
  toasts: {},
  rewardModal: {},
});

const TABS = [
  { id: 'page', label: 'Page Header' },
  { id: 'stats', label: 'Stats & Link' },
  { id: 'steps', label: 'How It Works' },
  { id: 'table', label: 'Table & Status' },
  { id: 'modal', label: 'Invite Modal' },
  { id: 'reward', label: 'Reward Popup' },
  { id: 'toasts', label: 'Toasts' },
];

const inputClass = 'w-full text-sm rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3 py-2 text-gray-900 dark:text-white';
const labelClass = 'block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1';

const Field = ({ label, value, onChange, multiline = false }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {multiline ? (
      <textarea className={inputClass} rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input className={inputClass} value={value || ''} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

const ReferAndEarnPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('page');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          statsLabels: { ...defaultForm().statsLabels, ...(c.statsLabels || {}) },
          referralLinkSection: { ...defaultForm().referralLinkSection, ...(c.referralLinkSection || {}) },
          howItWorks: {
            ...defaultForm().howItWorks,
            ...(c.howItWorks || {}),
            steps: (c.howItWorks?.steps?.length ? c.howItWorks.steps : defaultForm().howItWorks.steps).map((step, i) => ({
              ...emptyStep(i + 1, i),
              ...step,
              order: step.order ?? i,
            })),
          },
          referralsTable: {
            ...defaultForm().referralsTable,
            ...(c.referralsTable || {}),
            columns: { ...defaultForm().referralsTable.columns, ...(c.referralsTable?.columns || {}) },
          },
          statusLabels: { ...defaultForm().statusLabels, ...(c.statusLabels || {}) },
          emptyState: { ...defaultForm().emptyState, ...(c.emptyState || {}) },
          inviteModal: { ...defaultForm().inviteModal, ...(c.inviteModal || {}) },
          toasts: { ...defaultForm().toasts, ...(c.toasts || {}) },
          rewardModal: { ...defaultForm().rewardModal, ...(c.rewardModal || {}) },
        });
      }
    } catch {
      toastError('Failed to load Refer & Earn page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateNested = (section, field, value) =>
    setForm((prev) => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  const updateDeep = (section, subsection, field, value) =>
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: { ...prev[section][subsection], [field]: value },
      },
    }));

  const updateStep = (index, field, value) =>
    setForm((prev) => {
      const steps = [...prev.howItWorks.steps];
      steps[index] = { ...steps[index], [field]: value };
      return { ...prev, howItWorks: { ...prev.howItWorks, steps } };
    });

  const moveStep = (index, dir) =>
    setForm((prev) => {
      const steps = [...prev.howItWorks.steps];
      const next = index + dir;
      if (next < 0 || next >= steps.length) return prev;
      [steps[index], steps[next]] = [steps[next], steps[index]];
      return {
        ...prev,
        howItWorks: {
          ...prev.howItWorks,
          steps: steps.map((step, i) => ({ ...step, order: i, num: i + 1 })),
        },
      };
    });

  const addStep = () =>
    setForm((prev) => ({
      ...prev,
      howItWorks: {
        ...prev.howItWorks,
        steps: [...prev.howItWorks.steps, emptyStep(prev.howItWorks.steps.length + 1, prev.howItWorks.steps.length)],
      },
    }));

  const removeStep = (index) =>
    setForm((prev) => ({
      ...prev,
      howItWorks: {
        ...prev.howItWorks,
        steps: prev.howItWorks.steps.filter((_, i) => i !== index).map((step, i) => ({ ...step, order: i, num: i + 1 })),
      },
    }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) toastSuccess('Refer & Earn page saved');
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore preloaded Refer & Earn defaults?')) return;
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Defaults restored');
      await fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title="Refer & Earn Page"
        subtitle="Edit referral program page copy, steps, table labels, modals, and toast messages"
        icon={LuGift}
        onBack={() => navigate('/portal-cms')}
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 space-y-4 mb-6">
        {activeTab === 'page' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Page title" value={form.pageTitle} onChange={(v) => updateField('pageTitle', v)} />
            <Field label="Page icon" value={form.pageIcon} onChange={(v) => updateField('pageIcon', v)} />
            <div className="md:col-span-2">
              <Field label="Page subtitle" value={form.pageSubtitle} onChange={(v) => updateField('pageSubtitle', v)} multiline />
            </div>
            <Field label="Loading message" value={form.loadingMessage} onChange={(v) => updateField('loadingMessage', v)} />
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Total referrals label" value={form.statsLabels.totalReferrals} onChange={(v) => updateNested('statsLabels', 'totalReferrals', v)} />
            <Field label="Pending label" value={form.statsLabels.pending} onChange={(v) => updateNested('statsLabels', 'pending', v)} />
            <Field label="Successful label" value={form.statsLabels.successful} onChange={(v) => updateNested('statsLabels', 'successful', v)} />
            <Field label="Reward points label" value={form.statsLabels.rewardPoints} onChange={(v) => updateNested('statsLabels', 'rewardPoints', v)} />
            <div className="md:col-span-2"><hr className="border-gray-100 dark:border-dark-700" /></div>
            <div className="md:col-span-2">
              <Field label="Referral link section title" value={form.referralLinkSection.title} onChange={(v) => updateNested('referralLinkSection', 'title', v)} />
            </div>
            <div className="md:col-span-2">
              <Field label="Referral link description" value={form.referralLinkSection.description} onChange={(v) => updateNested('referralLinkSection', 'description', v)} multiline />
            </div>
            <Field label="Your code prefix" value={form.referralLinkSection.yourCodePrefix} onChange={(v) => updateNested('referralLinkSection', 'yourCodePrefix', v)} />
            <Field label="Copy code button" value={form.referralLinkSection.copyCodeLabel} onChange={(v) => updateNested('referralLinkSection', 'copyCodeLabel', v)} />
            <Field label="Send invite button" value={form.referralLinkSection.sendInviteButton} onChange={(v) => updateNested('referralLinkSection', 'sendInviteButton', v)} />
          </div>
        )}

        {activeTab === 'steps' && (
          <div className="space-y-4">
            <Field label="Section title" value={form.howItWorks.title} onChange={(v) => updateNested('howItWorks', 'title', v)} />
            {form.howItWorks.steps.map((step, index) => (
              <div key={index} className="p-4 rounded-xl border border-gray-100 dark:border-dark-700 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">Step {step.num}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveStep(index, -1)} className="p-1.5 rounded hover:bg-gray-100"><FaArrowUp className="w-3 h-3" /></button>
                    <button type="button" onClick={() => moveStep(index, 1)} className="p-1.5 rounded hover:bg-gray-100"><FaArrowDown className="w-3 h-3" /></button>
                    <button type="button" onClick={() => removeStep(index)} className="p-1.5 rounded text-red-500"><FaTrash className="w-3 h-3" /></button>
                  </div>
                </div>
                <Field label="Title" value={step.title} onChange={(v) => updateStep(index, 'title', v)} />
                <Field label="Description" value={step.description} onChange={(v) => updateStep(index, 'description', v)} multiline />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={step.isActive !== false} onChange={(e) => updateStep(index, 'isActive', e.target.checked)} />
                  Active
                </label>
              </div>
            ))}
            <button type="button" onClick={addStep} className="flex items-center gap-2 text-sm text-primary-600 font-medium">
              <FaPlus className="w-3 h-3" /> Add step
            </button>
          </div>
        )}

        {activeTab === 'table' && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Field label="Table title" value={form.referralsTable.title} onChange={(v) => updateNested('referralsTable', 'title', v)} />
            </div>
            <Field label="Column: Email" value={form.referralsTable.columns.email} onChange={(v) => updateDeep('referralsTable', 'columns', 'email', v)} />
            <Field label="Column: Name" value={form.referralsTable.columns.name} onChange={(v) => updateDeep('referralsTable', 'columns', 'name', v)} />
            <Field label="Column: Status" value={form.referralsTable.columns.status} onChange={(v) => updateDeep('referralsTable', 'columns', 'status', v)} />
            <Field label="Column: Invited On" value={form.referralsTable.columns.invitedOn} onChange={(v) => updateDeep('referralsTable', 'columns', 'invitedOn', v)} />
            <Field label="Column: Registered" value={form.referralsTable.columns.registered} onChange={(v) => updateDeep('referralsTable', 'columns', 'registered', v)} />
            <Field label="Column: Reward" value={form.referralsTable.columns.reward} onChange={(v) => updateDeep('referralsTable', 'columns', 'reward', v)} />
            <div className="md:col-span-2"><hr className="border-gray-100 dark:border-dark-700" /></div>
            <Field label="Status: Pending" value={form.statusLabels.pending} onChange={(v) => updateNested('statusLabels', 'pending', v)} />
            <Field label="Status: Registered" value={form.statusLabels.registered} onChange={(v) => updateNested('statusLabels', 'registered', v)} />
            <Field label="Status: Purchased" value={form.statusLabels.purchased} onChange={(v) => updateNested('statusLabels', 'purchased', v)} />
            <Field label="Status: Rewarded" value={form.statusLabels.rewarded} onChange={(v) => updateNested('statusLabels', 'rewarded', v)} />
            <div className="md:col-span-2">
              <Field label="Empty state description" value={form.emptyState.description} onChange={(v) => updateNested('emptyState', 'description', v)} multiline />
            </div>
          </div>
        )}

        {activeTab === 'modal' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Modal title" value={form.inviteModal.title} onChange={(v) => updateNested('inviteModal', 'title', v)} />
            <Field label="Modal subtitle" value={form.inviteModal.subtitle} onChange={(v) => updateNested('inviteModal', 'subtitle', v)} />
            <Field label="Email label" value={form.inviteModal.emailLabel} onChange={(v) => updateNested('inviteModal', 'emailLabel', v)} />
            <Field label="Email placeholder" value={form.inviteModal.emailPlaceholder} onChange={(v) => updateNested('inviteModal', 'emailPlaceholder', v)} />
            <Field label="Name label" value={form.inviteModal.nameLabel} onChange={(v) => updateNested('inviteModal', 'nameLabel', v)} />
            <Field label="Name placeholder" value={form.inviteModal.namePlaceholder} onChange={(v) => updateNested('inviteModal', 'namePlaceholder', v)} />
            <Field label="Cancel button" value={form.inviteModal.cancelLabel} onChange={(v) => updateNested('inviteModal', 'cancelLabel', v)} />
            <Field label="Submit button" value={form.inviteModal.submitLabel} onChange={(v) => updateNested('inviteModal', 'submitLabel', v)} />
            <Field label="Submitting label" value={form.inviteModal.submittingLabel} onChange={(v) => updateNested('inviteModal', 'submittingLabel', v)} />
          </div>
        )}

        {activeTab === 'reward' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Popup title" value={form.rewardModal.title} onChange={(v) => updateNested('rewardModal', 'title', v)} />
            <Field label="Subtitle highlight" value={form.rewardModal.subtitleHighlight} onChange={(v) => updateNested('rewardModal', 'subtitleHighlight', v)} />
            <Field label="Card title" value={form.rewardModal.cardTitle} onChange={(v) => updateNested('rewardModal', 'cardTitle', v)} />
            <Field label="Card badge" value={form.rewardModal.cardBadge} onChange={(v) => updateNested('rewardModal', 'cardBadge', v)} />
            <div className="md:col-span-2">
              <Field label="Card body" value={form.rewardModal.cardBody} onChange={(v) => updateNested('rewardModal', 'cardBody', v)} multiline />
            </div>
            <Field label="CTA button" value={form.rewardModal.ctaButton} onChange={(v) => updateNested('rewardModal', 'ctaButton', v)} />
            <Field label="Skip label" value={form.rewardModal.skipLabel} onChange={(v) => updateNested('rewardModal', 'skipLabel', v)} />
            <div className="md:col-span-2">
              <Field label="Footer note" value={form.rewardModal.footerNote} onChange={(v) => updateNested('rewardModal', 'footerNote', v)} />
            </div>
          </div>
        )}

        {activeTab === 'toasts' && (
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Load error" value={form.toasts.loadError} onChange={(v) => updateNested('toasts', 'loadError', v)} />
            <Field label="Link copied" value={form.toasts.linkCopied} onChange={(v) => updateNested('toasts', 'linkCopied', v)} />
            <Field label="Code copied" value={form.toasts.codeCopied} onChange={(v) => updateNested('toasts', 'codeCopied', v)} />
            <Field label="Email required" value={form.toasts.emailRequired} onChange={(v) => updateNested('toasts', 'emailRequired', v)} />
            <Field label="Email invalid" value={form.toasts.emailInvalid} onChange={(v) => updateNested('toasts', 'emailInvalid', v)} />
            <Field label="Invite success" value={form.toasts.inviteSuccess} onChange={(v) => updateNested('toasts', 'inviteSuccess', v)} />
            <Field label="Invite error" value={form.toasts.inviteError} onChange={(v) => updateNested('toasts', 'inviteError', v)} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={handleSave} disabled={saving} className="btn-premium flex items-center gap-2">
          <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button type="button" onClick={handleReset} disabled={resetting} className="px-4 py-2 rounded-xl border text-sm font-medium flex items-center gap-2">
          <FaUndo /> {resetting ? 'Resetting...' : 'Reset to Defaults'}
        </button>
        <label className="flex items-center gap-2 text-sm ml-auto cursor-pointer">
          <input type="checkbox" checked={form.isPublished !== false} onChange={(e) => updateField('isPublished', e.target.checked)} />
          Published
        </label>
      </div>
    </PageLayout>
  );
};

export default ReferAndEarnPortalCMS;
